import axios from 'axios'
import { Message, MessageBox } from 'element-ui'
import router from '@/router'
import store from '@/store'
import { getToken, setToken, removeToken } from '@/utils/auth'
import { refreshToken } from '@/api/auth'
import { getLoginPathByRoutePath } from '@/utils/device'

const request = axios.create({
    baseURL: process.env.VUE_APP_BASE_API || '',
    timeout: 30000
})

// 防止 401 时重复弹多个框
let isReloginShown = false
// 防并发重复刷新 Token 的锁
let refreshingPromise = null

/**
 * 解析 JWT payload
 */
function parseJwtPayload(token) {
    try {
        const parts = token.split('.')
        if (parts.length !== 3) {
            return null
        }

        const payload = parts[1]
            .replace(/-/g, '+')
            .replace(/_/g, '/')

        const decoded = decodeURIComponent(
            atob(payload)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        )

        return JSON.parse(decoded)
    } catch (e) {
        return null
    }
}

// 判断 token 是否即将过期 (例如距离过期不到 15 分钟)
function isTokenExpiringSoon(token) {
    const payload = parseJwtPayload(token)
    if (!payload || !payload.exp) {
        return false
    }
    const nowSeconds = Math.floor(Date.now() / 1000)
    const remainSeconds = payload.exp - nowSeconds

    // 【修改点 4】：去掉 remainSeconds > 0 的限制
    // 只要剩余时间少于 15 分钟（哪怕是负数，即已经过期了），前端都尝试发起无感续期
    return remainSeconds < 15 * 60
}

/**
 * 统一处理登录过期
 */
function handleLoginExpired() {
    removeToken()
    store.commit('CLEAR_USER_INFO')

    if (isReloginShown) return

    isReloginShown = true

    MessageBox.confirm(
        '你的登录已过期，为了账号安全，请重新登录',
        '登录提示',
        {
            type: 'warning',
            confirmButtonText: '重新登录',
            cancelButtonText: '取消',
            closeOnClickModal: false,
            closeOnPressEscape: false
        }
    ).finally(() => {
        const loginPath = getLoginPathByRoutePath(router.currentRoute.path)

        if (router.currentRoute.path !== loginPath) {
            router.replace(loginPath)
        }
        isReloginShown = false
    })
}

request.interceptors.request.use(
    async config => {
        let token = getToken()

        const skipRefreshUrls = [
            '/api/auth/login',
            '/api/auth/captcha',
            '/api/auth/refresh'
        ]
        const isSkipUrl = skipRefreshUrls.some(url => config.url && config.url.includes(url))

        // 1. 刷新接口本身不做“自动刷新判断”，但仍然要带旧 token
        if (token && isSkipUrl) {
            if (config.url.includes('/api/auth/refresh')) {
                config.headers['Authorization'] = 'Bearer ' + token
            }
            return config
        }

        // 2. 普通接口：如果 token 快过期，先刷新
        if (token) {
            if (isTokenExpiringSoon(token)) {
                if (!refreshingPromise) {
                    refreshingPromise = refreshToken()
                        .then(res => {
                            // 后端返回格式：{ code, message, data: { token, tokenHead } }
                            const newToken = res?.data?.token
                            if (!newToken) {
                                throw new Error(res?.message || '刷新 token 失败')
                            }

                            setToken(newToken)
                            return newToken
                        })
                        .catch(err => {
                            console.error('Token 刷新失败', err)
                            removeToken()
                            store.commit('CLEAR_USER_INFO')
                            const loginPath = getLoginPathByRoutePath(router.currentRoute.path)

                            if (router.currentRoute.path !== loginPath) {
                                router.replace(loginPath)
                            }
                            throw err
                        })
                        .finally(() => {
                            refreshingPromise = null
                        })
                }

                try {
                    token = await refreshingPromise
                } catch (e) {
                    return Promise.reject(e)
                }
            }

            config.headers['Authorization'] = 'Bearer ' + token
        }

        return config
    },
    error => Promise.reject(error)
)

request.interceptors.response.use(
    response => {
        if (response.config.responseType === 'blob') {
            return response.data
        }

        const res = response.data

        if (res?.code === 401) {
            handleLoginExpired()
            return Promise.resolve({
                code: 401,
                message: res.message || '未登录或登录已失效',
                data: null,
                _handled401: true
            })
        }

        if (res?.code === 403) {
            Message.error(res.message || '无权限访问')
            return Promise.reject(new Error(res.message || '无权限访问'))
        }

        return res
    },

    error => {
        const res = error.response?.data
        const status = error.response?.status

        if (status === 401 || res?.code === 401) {
            handleLoginExpired()
            return Promise.resolve({
                code: 401,
                message: res?.message || '未登录或登录已失效',
                data: null,
                _handled401: true
            })
        }

        if (status === 403 || res?.code === 403) {
            Message.error(res?.message || '无权限访问')
            return Promise.reject(error)
        }

        Message.error(res?.message || '请求失败，请稍后重试')
        return Promise.reject(error)
    }
)

export default request